import React, { useState, forwardRef, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import HomeIcon from '@material-ui/icons/Home';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import FolderIcon from '@material-ui/icons/Folder';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import CreateNewFolderIcon from '@material-ui/icons/CreateNewFolder';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import SubdirectoryArrowLeftIcon from '@material-ui/icons/SubdirectoryArrowLeft';

import Button from '../UI/Button';
import Loader from '../UI/Loader';
import Tooltip from '../UI/Tooltip';

import { FolderContext } from '../../context/folders/folderState';
import { NotificationContext } from '../../context/notifications/notificationState';
import { getAllParentFolders } from '../../utils';

const FoldersPopup = forwardRef(({ onCancel, onOpenFolderModal, folders, foldersLoading, onMoveToFolder, loading, data, resource, fromSidebar, onMoveResourceToFolder }, ref) => {
  const [level, setLevel] = useState('root');
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [dataArr, setDataArr] = useState([]);
  const { setParentFolder, updateFolder } = useContext(FolderContext);
  const { setNotification } = useContext(NotificationContext);
  // const [draggedFolder, setDraggedFolder] = useState(null);

  useEffect(() => {
    const nonDeletedTemplates = filterNonDeletedTemplates(data);
    const arr = [];
    for(let key in nonDeletedTemplates) {
      arr.push({ ...nonDeletedTemplates[key], id: key });
    }
    setDataArr(arr);
  }, [data]);

  const filterNonDeletedTemplates = (tmplts) => {
    const filtered = {};
    for(let key in tmplts) {
      if(!tmplts[key].deleted) {
        filtered[key] = tmplts[key];
      }
    }
    return filtered;
  }

  const folderSelectHandler = (selected, level, e) => {
    // console.log(e.target.parentElement.parentElement.parentElement.parentElement.parentElement);
    setSelectedFolder(selected);
    setLevel(level);
    // onKeyUpdate(new Date().getTime());
  }

  const openFolderModalHandler = () => {
    onOpenFolderModal();
    // onCancel();
    setParentFolder(selectedFolder);
  }

  const goToPrevFolderHandler = () => {
    if(selectedFolder.parentFolder) {
      const folder = [...folders].filter(folder => folder.id === selectedFolder.parentFolder)[0];
      setSelectedFolder(folder);
    }else {
      setLevel('root');
      setSelectedFolder(null);
    }
  }

  // Drag start
  const dragStartHandler = (e, folder) => {
    // setDraggedFolder(folder);
    const div = document.createElement('div');
    div.id = 'draggable-folder-el';
    div.className = 'draggable-ghost-el';
    div.innerText = `Déplacer le ${folder.name}`;
    document.getElementById('root').appendChild(div);
    e.dataTransfer.setDragImage(div, 0, 0);
    e.dataTransfer.setData('folder', JSON.stringify(folder));
  }
  
  // Drag over
  const dragOverHandler = (e) => {
    e.preventDefault();
    const folderBox = e.target.closest('.folder-list-item');
    if(!folderBox.classList.contains('hover')) {
      folderBox.classList.add('hover');
    }
  }

  // Drag leave
  const dragLeaveHandler = (e) => {
    e.preventDefault();
    const folderBox = e.target.closest('.folder-list-item');
    if(folderBox.classList.contains('hover')) {
      folderBox.classList.remove('hover');
    }
  }
  
  // Drop
  const dropHandler = async (e, folder) => {
    const data = e.dataTransfer.getData('folder') ? JSON.parse(e.dataTransfer.getData('folder')) : null;

    const folderBox = e.target.closest('.folder-list-item');
    if(folderBox.classList.contains('hover')) {
      folderBox.classList.remove('hover');
    }

    if(data) {
      const parentFolders = getAllParentFolders(folders, folder);
      if(folder.id === data.id) 
        return setNotification({ msg: `Impossible de déplacer le dossier "${data.name}" vers le dossier "${folder.name}"`, type: 'warning' });
      if(folder.parentFolder === data.id) 
        return setNotification({ msg: `Impossible de déplacer le dossier "${data.name}" vers le dossier "${[...folders].find(f => f.id === folder.parentFolder).name}"`, type: 'warning' });
      if(data.parentFolder === folder.id) 
        return setNotification({ msg: `Impossible de déplacer le dossier "${data.name}" vers le dossier "${[...folders].find(f => f.id === data.parentFolder).name}"`, type: 'warning' });
      if(parentFolders.find(f => f.id === data.id)) 
        return setNotification({ msg: `Impossible de déplacer le dossier "${data.name}" vers le dossier "${parentFolders.find(f => f.id === data.id).name}"`, type: 'warning' });
  
      console.log(`Move ${data.name} to ${folder.name}`);
      await updateFolder({ parentFolder: folder.id }, data.id);
      setNotification({ msg: `Dossier "${data.name}" déplacé dans le dossier "${folder.name}"`, type: 'default' });
      return;
    }

    const templateData = e.dataTransfer.getData('template') ? JSON.parse(e.dataTransfer.getData('template')) : null;

    if(templateData) {
      onMoveResourceToFolder(templateData, folder);
    }
  }

  // Drag end 
  const dragEndHandler = (e) => {
    document.getElementById('draggable-folder-el').remove();
  }

  const moveUpOneLevelHandler = async () => {
    const f = [...folders].find(f => f.id === selectedFolder.parentFolder);
    await updateFolder({ parentFolder: f.parentFolder ? f.parentFolder : null }, selectedFolder.id);
    setLevel('root');
  }

  const moveToRootFolderHandler = async () => {
    await updateFolder({ parentFolder: null }, selectedFolder.id);
    setLevel('root');
  }

  return (
    <div className="folders-popup" ref={ref}>
      <div className={level === 'root' ? "folders-popup__head root" : "folders-popup__head inner"}>
        <div className="main">
          <span className="icon">
            <HomeIcon />
          </span>
          <h5>Biblitohèque</h5>
        </div>
        <div className="back">
          <span className="icon" onClick={goToPrevFolderHandler}>
            <ArrowBackIcon  />  
          </span>
          <h5>{selectedFolder && selectedFolder.name}</h5>
          {/* {selectedFolder && selectedFolder.parentFolder && 
            <div className="back__actions">
              <div className="tooltip-wrapper">
                <Tooltip text="Move up one level" />
                <span className="icon" onClick={moveUpOneLevelHandler}><SubdirectoryArrowLeftIcon /></span>
              </div>
              <div className="tooltip-wrapper">
                <Tooltip text="Move to root folder" />
                <span className="icon" onClick={moveToRootFolderHandler}><ArrowUpwardIcon /></span>
              </div>
            </div>
          } */}
        </div>
      </div>
      <div className="folders-popup__body">
        {foldersLoading ? 
          <div style={{padding: '10px'}}><Loader normal primary small /></div> : 
          <ul className={level === 'root' ? "folders-popup__body_list root" : "folders-popup__body_list inner"}>
          {folders.length > 0 && folders.map((folder, i) => {
            if(!folder.parentFolder) {
              return <li 
                key={i} 
                className="root folder-list-item" 
                onDragStart={(e) => dragStartHandler(e, folder)} 
                onDragOver={dragOverHandler} 
                onDragLeave={dragLeaveHandler}
                onDrop={(e) => dropHandler(e, folder)} 
                onDragEnd={dragEndHandler}
                draggable
              >
                <div className="root" onClick={(e) => folderSelectHandler(folder, 'inner', e)}>
                  <FolderIcon />
                  <p>{folder.name}</p>
                  <span><KeyboardArrowRightIcon /></span>
                </div>
              </li>
            }else {
              return null;
            }
          })}
          {selectedFolder && dataArr.length > 0 && folders.map((folder, i) => {
            if(folder.parentFolder === selectedFolder.id) {
              return <li 
                key={i} 
                className="inner dont-hide-dropdown folder-list-item" 
                onClick={(e) => folderSelectHandler(folder, 'inner', e)}
                onDragStart={(e) => dragStartHandler(e, folder)} 
                onDragOver={dragOverHandler} 
                onDragLeave={dragLeaveHandler}
                onDrop={(e) => dropHandler(e, folder)} 
                onDragEnd={dragEndHandler}
                draggable
              >
              <div className="root dont-hide-dropdown">
                <FolderIcon className="dont-hide-dropdown" />
                <p className="dont-hide-dropdown">{folder.name}</p>
              </div>
            </li>
            }
            return null;
          })}
          {selectedFolder && dataArr.length > 0 && dataArr.map(resource => {
            if(resource.folderId && resource.folderId.length > 0 && resource.folderId.includes(selectedFolder.id) ) {
              return <li key={resource.id} className="inner">
              <div className="root">
                <DescriptionOutlinedIcon />
                <p>{resource.name}</p>
              </div>
            </li>
            }
            return null;
          })}
        </ul>}
      </div>
      <div className="folders-popup__foot">
        <div className="new" onClick={openFolderModalHandler}>
          <CreateNewFolderIcon />
        </div>
        <Button text="Annuler" onButtonClick={onCancel} outlinePrimary />
        <Button 
          text={!loading ? "Déplacer ici" : "Chargement ..."} 
          onButtonClick={() => fromSidebar ? onMoveToFolder(selectedFolder) : onMoveToFolder(resource, selectedFolder)} 
          disabled={loading}
          primary 
        />
      </div>
    </div>
  );
});

FoldersPopup.propTypes = {
  onCancel: PropTypes.func.isRequired
}

export default FoldersPopup;